import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { RuleBuilder } from "./RuleBuilder";
import { deleteTemplate, STRATEGIES_COPY as T, type Library, type Template } from "./strategies";
import { NoData, SectionHead, useStrategyWrite, type Editor } from "./StrategyParts";

/**
 * The rule library (v26 "Rule library", design call A): every (metric, operator, threshold, condition) tuple a rule uses, plus any
 * created up front, in C2's order and words, with how many strategies use it. Create new rule opens the builder under the table;
 * Delete asks C2 straight away when nothing uses the template, and shows the guard instead when something does (C2 refuses it
 * too, `template_in_use`, if the page is behind). Before C2 v1.17 serves templates the section says so and offers nothing.
 */
export function RuleLibrary({ library, editor }: { library: Library; editor: Editor }) {
  const [building, setBuilding] = useState(false);
  const [guard, setGuard] = useState<Template | null>(null);
  const remove = useStrategyWrite(editor, (t: Template) => deleteTemplate(t.id), (_answer, t) => T.done.templateDeleted(t.text));
  const templates = library.templates;
  return (
    <section className="flex flex-col gap-2" data-section="library">
      <SectionHead title={T.library.title}>{templates && <Button size="sm" onClick={() => setBuilding(true)}>{T.library.create}</Button>}</SectionHead>
      {!templates ? <p className="text-sm text-ks-grey-1">{T.library.unavailable}</p> : (
        <Card><CardContent className="py-2">
          <Table>
            <TableHeader><TableRow>{T.library.columns.map((c, i) => <TableHead key={i}>{c}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {templates.length === 0 && <TableRow><TableCell colSpan={3} className="text-ks-grey-1">{T.library.empty}</TableCell></TableRow>}
              {templates.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="whitespace-normal">{t.text}<NoData reason={t.no_data} /></TableCell>
                  <TableCell className={t.used_by_count ? "text-sm" : "text-sm text-ks-grey-1"} title={t.used_by.map((u) => u.name).join(", ") || undefined}>{T.library.usedBy(t.used_by_count)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="destructive" aria-label={T.library.deleteLabel(t.text)} disabled={remove.isPending}
                      onClick={() => (t.used_by_count > 0 ? setGuard(t) : remove.mutate(t))}>{T.library.delete}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}
      {building && <RuleBuilder mode={{ kind: "template", strategy: null }} library={library} editor={editor} onClose={() => setBuilding(false)} />}
      {guard && editor.anchor && <TemplateInUseDialog template={guard} container={editor.anchor} onClose={() => setGuard(null)} />}
    </section>
  );
}

/** v26's "Cannot delete this rule" guard: the strategies that use the template, and OK. Mounted in the tab's in-gate anchor. */
export function TemplateInUseDialog({ template, container, onClose }: { template: Template; container: HTMLElement; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent container={container} className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{T.library.inUseTitle}</DialogTitle>
          <DialogDescription className="text-ks-ink">{T.library.inUse(template.text, template.used_by.map((u) => u.name))}</DialogDescription>
        </DialogHeader>
        <DialogFooter><Button variant="outline" data-nonmutating onClick={onClose}>{T.library.ok}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
